import React, { useState, useEffect, useMemo } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Table } from "reactstrap";
import {
    Loading, WarningAlert, PaginationControls, ItemsPerPageSelector, 
    SortableHeaderCell, EntityRow, SearchBar, NotificationToast 
} from "../components/";
import { runWithNotification } from "../utils/runWithNotification";
import useSortableData from "../hooks/useSortableData";
import { pricesApi } from "../api/energyDataApi"
import { cronApi } from "../api/cronApi";
import DateRangePicker from "../components/DateRangePicker";
import "../App.css";

function roundMWh(rawWh) {
    const priceMWh = rawWh * 1e6;
    return Math.round(priceMWh + Number.EPSILON);
}

export function PricesComponent() {
    const country = "BE";
    const today = new Date();
    const [resolution, setResolution] = useState("hourly");

    const [dateRange, setDateRange] = useState({ from: today, to: today });
    const [prices, setPrices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState({
        visible: false,
        success: true,
        title: "",
        message: "",
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [pricesPerPage, setPricesPerPage] = useState(50);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!dateRange.from || !dateRange.to) return;
    setCurrentPage(1);

    const fetchPrices = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const start = new Date(Date.UTC(
            dateRange.from.getFullYear(),
            dateRange.from.getMonth(),
            dateRange.from.getDate(), 0, 0, 0, 0
        ));

        const end = new Date(Date.UTC(
            dateRange.to.getFullYear(),
            dateRange.to.getMonth(),
            dateRange.to.getDate(), 23, 59, 59, 999
        ));

        const data = await pricesApi.getData(
          start.toISOString(),
          end.toISOString(),
          country,
          resolution
        );

        setPrices(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || "Failed to fetch prices");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrices();
  }, [dateRange, country, resolution]);

  async function handleEntsoeFetch() {
        await runWithNotification({
        fn: async () => {
            await cronApi.triggerEntsoeData();
            const updatedPrices = await pricesApi.getData(
            dateRange.from.toISOString(),
            dateRange.to.toISOString(),
            country,
            resolution
            );
            setPrices(updatedPrices);
        },
        successMessage: "ENTSO-E fetch triggered successfully.",
        setNotification: setToast,
        errorMessage: "Failed to trigger ENTSO-E fetch.\nPlease try again later.",
        });
    }

    const pricesWithMwh = useMemo(
        () => prices.map((p) => ({ ...p, price_mwh: roundMWh(p.price) })),
        [prices]
    );

    const { items: sortedPrices, requestSort, sortConfig } = useSortableData(
        pricesWithMwh,
        { key: "", direction: "" }
    );



    const totalPages = Math.ceil(prices.length / pricesPerPage);
    const indexOfLastPrice = currentPage * pricesPerPage;
    const indexOfFirstPrice = indexOfLastPrice - pricesPerPage;
    const currentPrices = sortedPrices.slice(indexOfFirstPrice, indexOfLastPrice);

    function toggleDropdown() { setDropdownOpen(prevState => !prevState); }
    function handlePageChange(pageNumber) { setCurrentPage(pageNumber); }
    function handleDropdownChange(number) {
        setPricesPerPage(number);
        setCurrentPage(1);
    };

    const itemsPerPage = [50, 100];
    const filteredPrices = currentPrices.filter(price => {
        if (!searchTerm) return true;
        const lowerTerm = searchTerm.toLowerCase();
        return (
            (price.time && price.time.toString().toLowerCase().includes(lowerTerm)) ||
            (price.country_code && price.country_code.toString().toLowerCase().includes(lowerTerm)) ||
            (price.price && price.price.toString().toLowerCase().includes(lowerTerm)) ||
            (price.price_mwh && price.price_mwh.toString().toLowerCase().includes(lowerTerm))
        );
    });

    const displayError = error
        ? error.split("\n").map((line, index) => (
            <React.Fragment key={index}>
                {line}
                <br />
            </React.Fragment>
        ))
        : "Ooops! No prices found";

  if (isLoading) return <Loading />;

    return (
        <>
        <div className="d-flex justify-content-end align-items-center mb-3" style={{ gap: "0.5rem" }}>
            <DateRangePicker range={dateRange} onChange={setDateRange} />
            <div className="btn-group" role="group" aria-label="Resolution">
                <button
                type="button"
                className={`btn btn-light border ${resolution === "hourly" ? "active" : ""}`}
                onClick={() => setResolution("hourly")}
                >
                Hourly
                </button>
                <button
                type="button"
                className={`btn btn-light border ${resolution === "15min" ? "active" : ""}`}
                onClick={() => setResolution("15min")}
                >
                15-min
                </button>
            </div>
            <button
            className="btn btn-light border px-3 py-1"
            onClick={handleEntsoeFetch}
            >
            Fetch latest ENTSO-E data
            </button>
            <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
        </div>

        {filteredPrices.length > 0 ? (
            <>
            <Table bordered hover responsive size="sm" striped>
                <thead>
                <tr>
                    <SortableHeaderCell
                    columnKey={"time"}
                    label={"Time"}
                    currentSort={sortConfig}
                    onSort={requestSort}
                    />
                    <SortableHeaderCell
                    columnKey={"country_code"}
                    label={"Country code"}
                    currentSort={sortConfig}
                    onSort={requestSort}
                    />
                    <SortableHeaderCell
                    columnKey={"price"}
                    label={"Price [€/Wh]"}
                    currentSort={sortConfig}
                    onSort={requestSort}
                    />
                    <SortableHeaderCell
                    columnKey={"price_mwh"}
                    label={"Price [€/MWh]"}
                    currentSort={sortConfig}
                    onSort={requestSort}
                    />
                </tr>
                </thead>
                <tbody>
                {filteredPrices.map((price) => (
                    <EntityRow
                    key={price.time}
                    entity={price}
                    fields={["time", "country_code", "price", "price_mwh"]}
                    renderField={(field, value) => value}
                    />
                ))}
                </tbody>
            </Table>

            <div className="d-flex justify-content-between align-items-center">
                <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                />
                <ItemsPerPageSelector
                items={itemsPerPage}
                currentSelection={pricesPerPage}
                onSelect={handleDropdownChange}
                isOpen={dropdownOpen}
                toggle={toggleDropdown}
                />
            </div>
            </>
        ) : (
            <div className="d-flex flex-column align-items-center">
            <WarningAlert message={displayError} />
            </div>
        )}

        {toast.visible && (
            <NotificationToast
            icon={toast.success ? "check-circle" : "times-circle"}
            iconStyle={{
                color: toast.success ? "green" : "red",
                marginRight: "160px",
            }}
            title={toast.success ? "Success" : "Error"}
            message={toast.message}
            fade
            timeout={500}
            />
        )}
        </>
    );
}

export default withAuthenticationRequired(PricesComponent, {
    onRedirecting: () => <Loading />,
});
